/**
 * Scorer Console — Ball-by-ball scoring interface
 *
 * Flow stages (per innings):
 *   1. Pre-Toss Squad Selection (SquadSelectionPanel)
 *   2. Toss
 *   3. Set Openers (striker / non-striker / first bowler)
 *   4. Ball entry (striker/non-striker/bowler shown read-only; server manages strike rotation)
 *   5. Between overs → Set Next Bowler
 *   6. After wicket → New Batsman
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import SquadSelectionPanel from './SquadSelectionPanel';
import OverVisualization from './OverVisualization';
import BallEditModal from './BallEditModal';
import MatchCompletedBanner from './MatchCompletedBanner';
import SquadSwapModal from './SquadSwapModal';

const WICKET_TYPES = [
  { value: 'bowled', label: 'Bowled' },
  { value: 'caught', label: 'Caught' },
  { value: 'lbw', label: 'LBW' },
  { value: 'run_out', label: 'Run Out' },
  { value: 'stumped', label: 'Stumped' },
  { value: 'hit_wicket', label: 'Hit Wicket' },
  { value: 'retired_hurt', label: 'Retired Hurt (can return)' },
  { value: 'retired_out', label: 'Retired Out' },
];

// Extras → allowed wicket types (cricket-accurate)
const WICKET_TYPES_FOR_EXTRAS = {
  wide: ['stumped', 'run_out'],
  no_ball: ['run_out'],
};

const ordinalInnings = (n) => {
  if (n == null) return '';
  const map = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th' };
  return map[n] || `${n}th`;
};

export default function ScorerConsole({ eventId, fixtureId, fixture: initialFixture, currentUser, onRefresh: onRefreshProp, onMatchUpdate }) {
  const onRefresh = onRefreshProp || onMatchUpdate || (() => {});
  const isSuperAdmin = currentUser?.role === 'super_admin' || currentUser?.roles?.includes?.('super_admin');
  const [fixture, setFixture] = useState(initialFixture);
  const [innings, setInnings] = useState([]);
  const [currentInnings, setCurrentInnings] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [squadsData, setSquadsData] = useState(null);
  const [currentOverBalls, setCurrentOverBalls] = useState([]);
  const [allInningsBalls, setAllInningsBalls] = useState([]);
  const [showOverHistory, setShowOverHistory] = useState(false);
  const [eligibleBowlers, setEligibleBowlers] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [showEndInningsModal, setShowEndInningsModal] = useState(false);

  // Toss state
  const [tossWinnerTeamId, setTossWinnerTeamId] = useState('');
  const [tossDecision, setTossDecision] = useState('');

  // Openers picker state (used when innings has no striker_id yet)
  const [opener, setOpener] = useState({ striker_id: '', non_striker_id: '', bowler_id: '' });

  // Ball entry (server uses innings.striker_id/bowler_id — we only send runs/extras/wicket)
  const [ballData, setBallData] = useState(emptyBall());
  // Ball edit modal state (when scorer clicks an over-pill)
  const [editingBall, setEditingBall] = useState(null);
  // Mid-over bowler change
  const [showChangeBowler, setShowChangeBowler] = useState(false);
  // Replace-batsman modal: { position: 'striker' | 'non_striker', wrong_id, wrong_name }
  const [editBatsmanState, setEditBatsmanState] = useState(null);
  // Mid-match squad swap modal
  const [showSquadSwap, setShowSquadSwap] = useState(false);

  function emptyBall() {
    return {
      runs_scored: 0,
      overthrow_runs: 0,
      is_boundary_four: false,
      is_boundary_six: false,
      extras_type: null,
      extras_runs: 0,
      is_wicket: false,
      wicket_type: null,
      dismissed_player_id: null,
      fielder_id: null,
      shot_region: null,
      commentary: '',
    };
  }

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [fixtureRes, inningsRes, teamsRes, setupTeamsRes, poolRes, squadsRes] = await Promise.all([
        axiosInstance.get(`/v1/events/${eventId}/fixtures/${fixtureId}`),
        axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/innings`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${eventId}/auction/teams`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${eventId}/team-setup/teams`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${eventId}/auction/pool`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/squads`).catch(() => ({ data: null })),
      ]);

      setFixture(fixtureRes.data);
      setInnings(inningsRes.data || []);
      // Auction-enabled events → /auction/teams is source of truth.
      // Auction-disabled events → /auction/teams returns 403 (data:[]), fall back to
      // /team-setup/teams which writes to the same auction_teams table.
      const auctionTeams = teamsRes.data || [];
      const setupTeams = setupTeamsRes.data || [];
      setTeams(auctionTeams.length > 0 ? auctionTeams : setupTeams);
      setPlayers(poolRes.data || []);
      // Keep prior squadsData if refetch failed (don't nuke to null)
      if (squadsRes.data) setSquadsData(squadsRes.data);

      const active = (inningsRes.data || []).find(i => i.status === 'in_progress');
      setCurrentInnings(active || null);

      if (active) {
        // Dismissed list
        try {
          const d = await axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/innings/${active.id}/dismissed`);
          setDismissedIds(d.data?.dismissed_player_ids || []);
        } catch { setDismissedIds([]); }

        // Current over balls
        try {
          const ballsRes = await axiosInstance.get(
            `/v1/events/${eventId}/live/${fixtureId}/innings/${active.id}/balls`,
            { params: { over: active.current_over, limit: 12 } }
          );
          setCurrentOverBalls(ballsRes.data || []);
        } catch { setCurrentOverBalls([]); }

        // All balls of the innings (used for edit-batsman modal logic + super-admin Over History drill-in)
        try {
          const allRes = await axiosInstance.get(
            `/v1/events/${eventId}/live/${fixtureId}/innings/${active.id}/balls`,
            { params: { limit: 2000 } }
          );
          setAllInningsBalls(allRes.data || []);
        } catch { setAllInningsBalls([]); }

        // Eligible bowlers (only needed when current_bowler_id missing)
        if (!active.current_bowler_id) {
          try {
            const eb = await axiosInstance.get(
              `/v1/events/${eventId}/live/${fixtureId}/innings/${active.id}/eligible-bowlers`
            );
            setEligibleBowlers(eb.data?.candidates || []);
          } catch { setEligibleBowlers([]); }
        } else {
          setEligibleBowlers([]);
        }
      } else {
        setDismissedIds([]);
        setCurrentOverBalls([]);
        setAllInningsBalls([]);
        setEligibleBowlers([]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load match data');
    } finally {
      setLoading(false);
      setInitialFetchDone(true);
    }
  }, [eventId, fixtureId, isSuperAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Compute squad-restricted rosters. When a squad is locked for this match,
  // source the roster DIRECTLY from the squad payload so it works even when the
  // global /auction/pool endpoint is unavailable (auction-disabled events).
  const squadRostersByTeam = (() => {
    const map = {};
    (squadsData?.teams || []).forEach(t => {
      if (t.is_locked) {
        map[t.team_id] = (t.players || []).map(p => ({
          id: p.player_pool_id,
          player_name: p.player_name,
          user_profile_photo_url: p.player_photo || null,
          playing_role: p.playing_role || null,
          sold_to_team_id: t.team_id,
        }));
      }
    });
    return map;
  })();

  const teamRoster = (teamId) => {
    if (!teamId) return [];
    if (squadRostersByTeam[teamId]) return squadRostersByTeam[teamId];
    return players.filter(p => p.sold_to_team_id === teamId);
  };

  const battingRoster = teamRoster(currentInnings?.batting_team_id).filter(p => !dismissedIds.includes(p.id));
  const bowlingRoster = teamRoster(currentInnings?.bowling_team_id);
  const fixtureTeams = teams.filter(t => t.id === fixture?.team_a_id || t.id === fixture?.team_b_id);

  // ── Actions ─────────────────────────────────────────────────────────────
  const handleStartMatch = async () => {
    if (!tossWinnerTeamId || !tossDecision) { toast.error('Select toss winner and their decision'); return; }
    setLoading(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/start`,
        null, { params: { toss_winner_team_id: tossWinnerTeamId, toss_decision: tossDecision } }
      );
      toast.success('Match started!');
      onRefresh();
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start match'); }
    finally { setLoading(false); }
  };

  const handleSetOpeners = async () => {
    if (!opener.striker_id || !opener.non_striker_id || !opener.bowler_id) {
      toast.error('Select striker, non-striker, and opening bowler');
      return;
    }
    if (opener.striker_id === opener.non_striker_id) {
      toast.error('Striker and non-striker must be different');
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/openers`,
        opener
      );
      toast.success('Openers set');
      setOpener({ striker_id: '', non_striker_id: '', bowler_id: '' });
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to set openers'); }
    finally { setLoading(false); }
  };

  const handleSetBowler = async (bowlerId) => {
    setLoading(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/bowler`,
        { bowler_id: bowlerId }
      );
      toast.success('Bowler set');
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to set bowler'); }
    finally { setLoading(false); }
  };

  const handleNewBatsman = async (playerId) => {
    setLoading(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/new-batsman`,
        { player_pool_id: playerId }
      );
      toast.success('New batsman in');
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to bring new batsman'); }
    finally { setLoading(false); }
  };

  const handleUnsetBatsman = async (position) => {
    setLoading(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/unset-batsman`,
        { position }
      );
      toast.success('Batsman removed — pick the correct one');
      setEditBatsmanState(null);
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unset batsman'); }
    finally { setLoading(false); }
  };

  const handleReplaceBatsman = async (wrongId, correctId) => {
    setLoading(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/replace-batsman`,
        { wrong_player_id: wrongId, correct_player_id: correctId }
      );
      toast.success('Batsman replaced — innings recomputed');
      setEditBatsmanState(null);
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to replace batsman'); }
    finally { setLoading(false); }
  };

  const handleSwapStrike = async () => {
    // Manual swap: use openers endpoint with swapped values
    if (!currentInnings?.striker_id || !currentInnings?.non_striker_id || !currentInnings?.current_bowler_id) return;
    setLoading(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/openers`,
        {
          striker_id: currentInnings.non_striker_id,
          non_striker_id: currentInnings.striker_id,
          bowler_id: currentInnings.current_bowler_id,
        }
      );
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to swap'); }
    finally { setLoading(false); }
  };

  const handleEditBall = async (ball, overrides) => {
    const inningsId = ball.innings_id || currentInnings?.id;
    if (!inningsId) return;
    setLoading(true);
    try {
      await axiosInstance.patch(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${inningsId}/ball/${ball.id}`,
        overrides
      );
      toast.success('Ball updated & innings recomputed');
      setEditingBall(null);
      onRefresh(); await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to edit ball'); }
    finally { setLoading(false); }
  };

  const handleDeleteBall = async (ball) => {
    const inningsId = ball.innings_id || currentInnings?.id;
    if (!inningsId) return;
    setLoading(true);
    try {
      await axiosInstance.delete(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${inningsId}/ball/${ball.id}`
      );
      toast.success(`Ball ${ball.over_number}.${ball.ball_number} deleted & innings recomputed`);
      setEditingBall(null);
      onRefresh(); await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete ball'); }
    finally { setLoading(false); }
  };

  const handleChangeBowlerMidOver = async (bowlerId) => {
    if (!currentInnings) return;
    setLoading(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/bowler`,
        { bowler_id: bowlerId }
      );
      toast.success('Bowler changed');
      setShowChangeBowler(false);
      // Refetch eligible bowlers for mid-over context requires new fetch
      try {
        const eb = await axiosInstance.get(
          `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/eligible-bowlers`
        );
        setEligibleBowlers(eb.data?.candidates || []);
      } catch { /* noop */ }
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change bowler'); }
    finally { setLoading(false); }
  };

  // Force-load eligible bowlers when showing the change-bowler modal mid-over
  useEffect(() => {
    if (showChangeBowler && currentInnings) {
      (async () => {
        try {
          const eb = await axiosInstance.get(
            `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/eligible-bowlers`
          );
          setEligibleBowlers((eb.data?.candidates || []).filter(b => b.player_pool_id !== currentInnings.current_bowler_id));
        } catch { /* noop */ }
      })();
    }
  }, [showChangeBowler, currentInnings, eventId, fixtureId]);

  const handleRunClick = (runs) => {
    setBallData(prev => ({
      ...prev, runs_scored: runs,
      is_boundary_four: runs === 4, is_boundary_six: runs === 6,
    }));
  };

  const handleExtrasClick = (type) => {
    setBallData(prev => {
      const togglingOff = prev.extras_type === type;
      return {
        ...prev,
        extras_type: togglingOff ? null : type,
        extras_runs: 0,
        runs_scored: type === 'wide' ? 0 : prev.runs_scored,
        is_boundary_four: false,
        is_boundary_six: false,
      };
    });
  };

  const handleWicketClick = () => {
    setBallData(prev => ({
      ...prev,
      is_wicket: !prev.is_wicket,
      wicket_type: prev.is_wicket ? null : 'bowled',
      dismissed_player_id: prev.is_wicket ? null : currentInnings?.striker_id,
    }));
  };

  const handleRecordBall = async () => {
    if (!currentInnings) return;
    // Cricket rule: wicket type must match extras context
    if (ballData.is_wicket && ballData.extras_type === 'wide'
        && !WICKET_TYPES_FOR_EXTRAS.wide.includes(ballData.wicket_type)) {
      toast.error('On a wide, only stumping or run-out is allowed'); return;
    }
    if (ballData.is_wicket && ballData.extras_type === 'no_ball'
        && !WICKET_TYPES_FOR_EXTRAS.no_ball.includes(ballData.wicket_type)) {
      toast.error('On a no-ball, only run-out is allowed'); return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/ball`,
        ballData
      );
      toast.success('Ball recorded');
      if (res.data?.innings_ended) {
        const reasonLabels = { all_out: 'All Out', overs_complete: 'Overs Complete', target_reached: 'Target Reached' };
        toast.info(`Innings ended: ${reasonLabels[res.data.end_reason] || res.data.end_reason}`);
      }
      setBallData(emptyBall());
      onRefresh();
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record ball'); }
    finally { setLoading(false); }
  };

  const handleUndoBall = async () => {
    if (!currentInnings) return;
    if (!window.confirm('Undo last ball?')) return;
    setLoading(true);
    try {
      await axiosInstance.delete(`/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/ball/undo`);
      toast.success('Last ball undone');
      onRefresh(); await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to undo'); }
    finally { setLoading(false); }
  };

  const handleStartInnings = async (inningsNumber) => {
    if (teams.length < 2) return;
    let battingTeam, bowlingTeam;
    if (inningsNumber === 2 && innings.length >= 1) {
      battingTeam = innings[0].bowling_team_id;
      bowlingTeam = innings[0].batting_team_id;
    } else {
      battingTeam = teams[0]?.id; bowlingTeam = teams[1]?.id;
    }
    setLoading(true);
    try {
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/innings`,
        { innings_number: inningsNumber, batting_team_id: battingTeam, bowling_team_id: bowlingTeam }
      );
      toast.success(`Innings ${inningsNumber} started`);
      onRefresh(); await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start innings'); }
    finally { setLoading(false); }
  };

  const handleEndInnings = async (reason) => {
    if (!currentInnings) return;
    setLoading(true);
    try {
      const res = await axiosInstance.patch(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${currentInnings.id}/end`,
        null, { params: { reason } }
      );
      toast.success(`Innings ended (${reason.replace('_', ' ')})`);
      if (res.data?.match_tied_pending) {
        toast.info('Regulation tied — choose Super Over or Accept Tie');
      } else if (res.data?.match_ended) {
        toast.success(`Match ended: ${res.data.result_summary || ''}`);
      }
      setShowEndInningsModal(false);
      onRefresh(); await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to end innings'); }
    finally { setLoading(false); }
  };

  const handleAcceptTie = async () => {
    if (!window.confirm('End the match as a TIE? This cannot be undone.')) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/finalise`,
        null, { params: { accept_tie: true } }
      );
      toast.success(res.data?.result_summary || 'Match finalised');
      onRefresh(); await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to finalise match'); }
    finally { setLoading(false); }
  };

  const handleTriggerSuperOver = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/super-over`
      );
      toast.success(`Super Over started (Innings ${res.data?.innings_number})`);
      onRefresh(); await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start Super Over'); }
    finally { setLoading(false); }
  };

  const handleFinaliseMatch = async () => {
    if (!window.confirm('Finalise the match now? This will recompute the result from the recorded balls and close the match.')) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/finalise`
      );
      toast.success(res.data?.result_summary || 'Match finalised');
      onRefresh(); await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to finalise match'); }
    finally { setLoading(false); }
  };

  // ── Stage detection ──────────────────────────────────────────────────────
  const needsOpeners = currentInnings && !currentInnings.striker_id && !currentInnings.non_striker_id;
  // Need a new batsman whenever exactly one slot is empty (after the openers
  // were picked). Independent of bowler state — the wicket-on-last-ball case
  // can blank both fields simultaneously, and we still want the batsman
  // picker to surface first.
  const needsNewBatsman = currentInnings && !needsOpeners
                          && (!currentInnings.striker_id || !currentInnings.non_striker_id);
  // Need a new bowler when both batsmen are at the crease but no bowler is set.
  const needsBowler = currentInnings && !!currentInnings.striker_id && !!currentInnings.non_striker_id
                      && !currentInnings.current_bowler_id;
  const canEnterBall = currentInnings && currentInnings.striker_id && currentInnings.non_striker_id
                       && currentInnings.current_bowler_id;

  const allowedWicketTypes = ballData.extras_type && WICKET_TYPES_FOR_EXTRAS[ballData.extras_type]
    ? WICKET_TYPES.filter(w => WICKET_TYPES_FOR_EXTRAS[ballData.extras_type].includes(w.value))
    : WICKET_TYPES;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="space-y-6"
      style={{ paddingBottom: 'calc(var(--bottom-nav-h) + 5rem)' }}
      data-testid="scorer-console"
    >
      {/* Pre-Toss Squad Selection + Toss */}
      {initialFetchDone && !currentInnings && innings.length === 0 && (
        <>
          <SquadSelectionPanel eventId={eventId} fixtureId={fixtureId} onLocked={fetchData} />
          {!squadsData?.both_locked ? (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center text-sm text-gray-500" data-testid="toss-gated">
              <i className="ph-bold ph-lock mr-2"></i>Toss will unlock once both squads are locked.
            </div>
          ) : (
            <div className="bg-[#2B3A67] rounded-xl p-6" data-testid="toss-form">
              <h3 className="text-white font-bold text-lg mb-4 text-center">
                <i className="ph-bold ph-coin mr-2"></i>Toss &amp; Match Start
              </h3>
              <div className="mb-4">
                <label className="text-white/70 text-sm block mb-2">Who won the toss?</label>
                <div className="grid grid-cols-2 gap-3">
                  {fixtureTeams.map(team => (
                    <button
                      key={team.id}
                      onClick={() => setTossWinnerTeamId(team.id)}
                      className={`py-3 px-4 rounded-xl font-bold text-sm transition-all ${
                        tossWinnerTeamId === team.id
                          ? 'bg-[#3AAFA9] text-white ring-2 ring-[#3AAFA9]/50'
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      }`}
                      data-testid={`toss-team-${team.id}`}
                    >
                      {team.team_logo_url && <img src={team.team_logo_url} alt="" className="w-6 h-6 rounded-full inline mr-2" />}
                      {team.team_name}
                    </button>
                  ))}
                </div>
              </div>
              {tossWinnerTeamId && (
                <div className="mb-5">
                  <label className="text-white/70 text-sm block mb-2">Elected to</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setTossDecision('bat')}
                      className={`py-3 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 ${
                        tossDecision === 'bat' ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'
                      }`}
                      data-testid="toss-bat"
                    ><i className="ph-bold ph-baseball"></i>Bat</button>
                    <button
                      onClick={() => setTossDecision('bowl')}
                      className={`py-3 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 ${
                        tossDecision === 'bowl' ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'
                      }`}
                      data-testid="toss-bowl"
                    ><i className="ph-bold ph-target"></i>Bowl</button>
                  </div>
                </div>
              )}
              <button
                ref={el => {
                  // Auto-scroll button into view once BOTH toss inputs are selected
                  if (el && tossWinnerTeamId && tossDecision) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
                onClick={handleStartMatch}
                disabled={loading || !tossWinnerTeamId || !tossDecision}
                className="w-full py-3 bg-[#3AAFA9] text-white font-bold rounded-xl hover:bg-[#2D8A85] disabled:opacity-40"
                data-testid="start-match-btn"
              >{loading ? 'Starting...' : 'Start Match'}</button>
            </div>
          )}
        </>
      )}

      {/* 2nd innings kickoff / tie-pending actions / match completed */}
      {!currentInnings && innings.length > 0 && innings.every(i => i.status === 'completed' || i.status === 'declared') && (
        <>
          {innings.length === 1 ? (
            <div className="bg-[#2B3A67] rounded-xl p-4 text-center">
              <p className="text-white/70 mb-3">1st innings completed. Start 2nd innings.</p>
              <button
                onClick={() => handleStartInnings(2)}
                disabled={loading}
                className="px-6 py-2.5 bg-[#3AAFA9] text-white font-bold rounded-lg hover:bg-[#2D8A85]"
                data-testid="start-2nd-innings-btn"
              >Start 2nd Innings</button>
            </div>
          ) : fixture?.match_status === 'live' ? (
            (() => {
              // Compute actual tie state from innings totals before deciding which
              // panel to render. After a `reopen` the match status flips back to
              // 'live' even when the original result wasn't a tie — so we must not
              // assume "2 innings + live = tied".
              const teamARuns = innings
                .filter((i) => i.batting_team_id === fixture?.team_a_id)
                .reduce((s, i) => s + (i.total_runs || 0), 0);
              const teamBRuns = innings
                .filter((i) => i.batting_team_id === fixture?.team_b_id)
                .reduce((s, i) => s + (i.total_runs || 0), 0);
              const allInningsCompleted = innings.every(
                (i) => i.status === 'completed' || i.status === 'declared',
              );
              const isActuallyTied = allInningsCompleted && teamARuns === teamBRuns;

              if (isActuallyTied) {
                return (
                  <TiePendingPanel
                    eventId={eventId}
                    fixtureId={fixtureId}
                    loading={loading}
                    onAccept={handleAcceptTie}
                    onSuperOver={handleTriggerSuperOver}
                  />
                );
              }
              return (
                <ReopenedMatchPanel
                  fixture={fixture}
                  teams={teams}
                  innings={innings}
                  teamARuns={teamARuns}
                  teamBRuns={teamBRuns}
                  loading={loading}
                  onFinalise={handleFinaliseMatch}
                  eventId={eventId}
                  fixtureId={fixtureId}
                  onBallClick={setEditingBall}
                />
              );
            })()
          ) : (
            <MatchCompletedBanner
              innings={innings}
              fixture={fixture}
              eventId={eventId}
              fixtureId={fixtureId}
            />
          )}
        </>
      )}

      {/* Current Score — visual but tight (team identity is in the sticky header) */}
      {currentInnings && (
        <div className="bg-[#2B3A67] rounded-xl px-4 py-3" data-testid="score-banner">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-[12px] font-semibold text-white/85 truncate" data-testid="score-banner-team">
              {currentInnings.batting_team_name}
            </p>
            <span className="text-[10px] uppercase tracking-wide font-bold text-amber-200/90 whitespace-nowrap flex-shrink-0">
              {ordinalInnings(currentInnings.innings_number)} Innings
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-3xl font-bold text-white tabular-nums leading-none">
              {currentInnings.total_runs}<span className="text-white/80 text-2xl">/{currentInnings.total_wickets}</span>
            </p>
            <p className="text-white/70 text-xs tabular-nums whitespace-nowrap">
              {currentInnings.total_overs} ov · RR {currentInnings.run_rate}
            </p>
          </div>
          {currentInnings.target_runs && (
            <div className="bg-white/10 rounded-md mt-2 px-3 py-1.5 text-center text-[12px] text-white/90 tabular-nums">
              Target <span className="font-semibold">{currentInnings.target_runs}</span>
              <span className="text-white/40 mx-1.5">·</span>
              Need <span className="font-semibold text-amber-200">{currentInnings.target_runs - currentInnings.total_runs}</span>
              {currentInnings.required_rate && (
                <>
                  <span className="text-white/40 mx-1.5">·</span>
                  RRR <span className="font-semibold">{currentInnings.required_rate}</span>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* On-field banner (striker / non-striker / bowler) */}
      {currentInnings && canEnterBall && (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-2.5 grid grid-cols-3 gap-2 text-sm" data-testid="onfield-banner">
          <div className="flex flex-col min-w-0" data-testid="striker-indicator">
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Striker *</span>
            <div className="flex items-center gap-1 min-w-0">
              <span className="font-bold text-[#2B3A67] truncate text-sm">{currentInnings.striker_name || '—'}</span>
              {currentInnings.striker_id && (
                <button
                  onClick={() => setEditBatsmanState({ position: 'striker', wrong_id: currentInnings.striker_id, wrong_name: currentInnings.striker_name })}
                  className="text-gray-400 hover:text-amber-600 flex-shrink-0"
                  title="Replace / remove this batsman"
                  data-testid="edit-striker-btn"
                >
                  <i className="ph-bold ph-pencil-simple text-xs"></i>
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col min-w-0" data-testid="non-striker-indicator">
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Non-Striker</span>
            <div className="flex items-center gap-1 min-w-0">
              <span className="font-medium text-gray-700 truncate text-sm">{currentInnings.non_striker_name || '—'}</span>
              {currentInnings.non_striker_id && (
                <button
                  onClick={() => setEditBatsmanState({ position: 'non_striker', wrong_id: currentInnings.non_striker_id, wrong_name: currentInnings.non_striker_name })}
                  className="text-gray-400 hover:text-amber-600 flex-shrink-0"
                  title="Replace / remove this batsman"
                  data-testid="edit-non-striker-btn"
                >
                  <i className="ph-bold ph-pencil-simple text-xs"></i>
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col min-w-0" data-testid="bowler-indicator">
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Bowler</span>
            <span className="font-medium text-gray-700 truncate text-sm">{currentInnings.current_bowler_name || '—'}</span>
          </div>
          <div className="col-span-3 pt-1.5 flex items-center justify-between gap-2 border-t border-gray-100">
            <div className="flex items-center gap-1.5 overflow-x-auto min-w-0 flex-1">
              <span className="text-[10px] text-gray-400 uppercase tracking-wide mr-1 flex-shrink-0">Over</span>
              <OverVisualization balls={currentOverBalls} onBallClick={(b) => setEditingBall(b)} />
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1 sm:gap-2 flex-shrink-0">
              <button
                onClick={() => setShowSquadSwap(true)}
                className="text-[11px] font-medium text-sky-600 hover:underline whitespace-nowrap text-right sm:text-left"
                title="Swap a squad player from the team's pool"
                data-testid="open-squad-swap-btn"
              >
                <i className="ph-bold ph-users-three mr-0.5"></i>Squads
              </button>
              <button
                onClick={() => setShowChangeBowler(true)}
                className="text-[11px] font-medium text-amber-600 hover:underline whitespace-nowrap text-right sm:text-left"
                data-testid="change-bowler-btn"
              >
                <i className="ph-bold ph-arrows-clockwise mr-0.5"></i>Bowler
              </button>
              <button
                onClick={handleSwapStrike}
                className="text-[11px] font-medium text-[#3AAFA9] hover:underline whitespace-nowrap text-right sm:text-left"
                data-testid="swap-strike-btn"
              >
                <i className="ph-bold ph-arrows-left-right mr-0.5"></i>Swap
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Over History — Super Admin only. Click any ball from any over to edit / delete. */}
      {isSuperAdmin && currentInnings && canEnterBall && allInningsBalls.length > 0 && (() => {
        // Group balls by over_number
        const overs = {};
        allInningsBalls.forEach(b => {
          (overs[b.over_number] = overs[b.over_number] || []).push(b);
        });
        const overNumbers = Object.keys(overs).map(Number).sort((a, b) => a - b);
        // Hide the currently-in-progress over (already shown above)
        const pastOverNumbers = overNumbers.filter(n => n !== currentInnings.current_over);
        if (pastOverNumbers.length === 0) return null;
        return (
          <div className="bg-white rounded-xl border border-amber-200 shadow-sm" data-testid="over-history-panel">
            <button
              onClick={() => setShowOverHistory(v => !v)}
              className="w-full px-3 py-2 flex items-center justify-between text-left"
              data-testid="over-history-toggle"
            >
              <span className="text-[11px] font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1.5">
                <i className="ph-bold ph-shield-check"></i>
                Super Admin · Over History ({pastOverNumbers.length} past {pastOverNumbers.length === 1 ? 'over' : 'overs'})
              </span>
              <i className={`ph-bold ph-caret-${showOverHistory ? 'up' : 'down'} text-amber-700`}></i>
            </button>
            {showOverHistory && (
              <div className="px-3 pb-3 pt-1 border-t border-amber-100 space-y-2 max-h-64 overflow-y-auto">
                <p className="text-[10px] text-gray-500">
                  Tap any ball to open the edit/delete dialog. Innings is deterministically replayed after each change.
                </p>
                {pastOverNumbers.map(overNum => (
                  <div key={overNum} className="flex items-center gap-2" data-testid={`over-history-over-${overNum}`}>
                    <span className="text-[11px] font-bold text-gray-500 w-10 flex-shrink-0">Ov {overNum}</span>
                    <div className="flex items-center gap-1.5 overflow-x-auto flex-1">
                      <OverVisualization balls={overs[overNum]} onBallClick={(b) => setEditingBall(b)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Openers Picker */}
      {needsOpeners && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3" data-testid="openers-form">
          <h4 className="font-bold text-[#2B3A67] flex items-center gap-2">
            <i className="ph-bold ph-users"></i>Set Openers
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Striker</label>
              <select
                value={opener.striker_id}
                onChange={e => setOpener(p => ({ ...p, striker_id: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="opener-striker"
              >
                <option value="">Select...</option>
                {battingRoster.map(p => <option key={p.id} value={p.id}>{p.player_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Non-Striker</label>
              <select
                value={opener.non_striker_id}
                onChange={e => setOpener(p => ({ ...p, non_striker_id: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="opener-non-striker"
              >
                <option value="">Select...</option>
                {battingRoster.filter(p => p.id !== opener.striker_id).map(p => (
                  <option key={p.id} value={p.id}>{p.player_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Opening Bowler</label>
              <select
                value={opener.bowler_id}
                onChange={e => setOpener(p => ({ ...p, bowler_id: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="opener-bowler"
              >
                <option value="">Select...</option>
                {bowlingRoster.map(p => <option key={p.id} value={p.id}>{p.player_name}</option>)}
              </select>
            </div>
          </div>
          <button
            onClick={handleSetOpeners}
            disabled={loading}
            className="w-full py-2.5 bg-[#3AAFA9] text-white font-bold rounded-lg hover:bg-[#2D8A85] disabled:opacity-50"
            data-testid="set-openers-btn"
          >{loading ? 'Saving...' : 'Start Over'}</button>
        </div>
      )}

      {/* Next Bowler Picker (between overs) */}
      {needsBowler && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3" data-testid="next-bowler-form">
          <h4 className="font-bold text-[#2B3A67] flex items-center gap-2">
            <i className="ph-bold ph-hand-arrow-up"></i>Select Next Bowler
          </h4>
          <p className="text-xs text-gray-500">
            Previous bowler is excluded. Bowlers at their cap are also excluded.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {eligibleBowlers.length === 0 && (
              <div className="col-span-2 text-center text-sm text-gray-400 py-4">No eligible bowlers available.</div>
            )}
            {eligibleBowlers.map(b => (
              <button
                key={b.player_pool_id}
                onClick={() => handleSetBowler(b.player_pool_id)}
                disabled={loading}
                className="text-left px-3 py-2 border border-gray-200 rounded-lg text-sm hover:border-[#3AAFA9] hover:bg-[#3AAFA9]/5 disabled:opacity-50"
                data-testid={`next-bowler-${b.player_pool_id}`}
              >
                <div className="font-medium text-gray-800 truncate">{b.player_name}</div>
                <div className="text-[11px] text-gray-500">Overs: {b.overs_bowled} / {b.max_overs}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* New Batsman Picker */}
      {needsNewBatsman && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3" data-testid="new-batsman-form">
          <h4 className="font-bold text-[#2B3A67] flex items-center gap-2">
            <i className="ph-bold ph-user-plus"></i>New Batsman In
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {battingRoster
              .filter(p => p.id !== currentInnings.striker_id && p.id !== currentInnings.non_striker_id)
              .map(p => (
                <button
                  key={p.id}
                  onClick={() => handleNewBatsman(p.id)}
                  disabled={loading}
                  className="text-left px-3 py-2 border border-gray-200 rounded-lg text-sm hover:border-[#3AAFA9] hover:bg-[#3AAFA9]/5 disabled:opacity-50"
                  data-testid={`new-batsman-${p.id}`}
                >
                  <div className="font-medium text-gray-800 truncate">{p.player_name}</div>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Ball Entry Panel */}
      {canEnterBall && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-2 space-y-2 pb-20" data-testid="ball-entry-panel">
          {/* Runs */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-12 flex-shrink-0">Runs</span>
            <div className="flex gap-1.5 flex-1">
              {[0, 1, 2, 3, 4, 6].map(runs => (
                <button
                  key={runs}
                  onClick={() => handleRunClick(runs)}
                  disabled={ballData.extras_type === 'wide'}
                  className={`flex-1 py-2 rounded-lg font-bold text-base transition-colors ${
                    ballData.runs_scored === runs
                      ? runs === 4 ? 'bg-blue-500 text-white'
                      : runs === 6 ? 'bg-purple-500 text-white'
                      : 'bg-[#3AAFA9] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50'
                  }`}
                  data-testid={`runs-${runs}`}
                >{runs}</button>
              ))}
            </div>
          </div>

          {/* Overthrows */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-12 flex-shrink-0">O/T</span>
            <div className="flex gap-1.5 flex-1">
              {[0, 1, 2, 3, 4].map(v => (
                <button
                  key={v}
                  onClick={() => setBallData(prev => ({ ...prev, overthrow_runs: v }))}
                  className={`flex-1 py-1.5 rounded-lg text-sm font-medium ${
                    ballData.overthrow_runs === v ? 'bg-amber-500 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }`}
                  data-testid={`overthrow-${v}`}
                >+{v}</button>
              ))}
            </div>
          </div>

          {/* Extras + Wicket row (compact 5-up grid on mobile) */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-12 flex-shrink-0">Extras</span>
            <div className="grid grid-cols-5 gap-1.5 flex-1">
              {[
                { key: 'wide', label: 'WD' },
                { key: 'no_ball', label: 'NB' },
                { key: 'bye', label: 'B' },
                { key: 'leg_bye', label: 'LB' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleExtrasClick(key)}
                  className={`py-1.5 rounded-lg text-sm font-bold transition-colors ${
                    ballData.extras_type === key ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`extra-${key}`}
                  title={key.replace('_', ' ').toUpperCase()}
                >{label}</button>
              ))}
              <button
                onClick={handleWicketClick}
                className={`py-1.5 rounded-lg text-sm font-bold transition-colors ${
                  ballData.is_wicket ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
                data-testid="wicket-btn"
              >W</button>
            </div>
          </div>
          {ballData.extras_type && (
              <div className="mt-2 flex items-center gap-2">
                <label className="text-xs text-gray-500 flex-1">
                  {ballData.extras_type === 'wide' ? '+ byes (beyond penalty):'
                    : ballData.extras_type === 'no_ball' ? '+ byes (beyond bat runs + penalty):'
                    : 'Byes/Leg-byes:'}
                </label>
                <input
                  type="number" min={0} max={6}
                  value={ballData.extras_runs}
                  onChange={e => setBallData(prev => ({ ...prev, extras_runs: parseInt(e.target.value) || 0 }))}
                  className="w-14 border border-gray-200 rounded px-2 py-1 text-sm"
                  data-testid="extras-runs-input"
                />
              </div>
            )}
            {ballData.is_wicket && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500 block mb-0.5 uppercase">Wicket Type</label>
                  <select
                    value={ballData.wicket_type || ''}
                    onChange={e => setBallData(prev => ({ ...prev, wicket_type: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                    data-testid="wicket-type-select"
                  >
                    {allowedWicketTypes.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 block mb-0.5 uppercase">Dismissed</label>
                  <select
                    value={ballData.dismissed_player_id || ''}
                    onChange={e => setBallData(prev => ({ ...prev, dismissed_player_id: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                    data-testid="dismissed-select"
                  >
                    <option value={currentInnings.striker_id}>Striker ({currentInnings.striker_name})</option>
                    <option value={currentInnings.non_striker_id}>Non-Striker ({currentInnings.non_striker_name})</option>
                  </select>
                </div>
                {['caught', 'stumped', 'run_out'].includes(ballData.wicket_type) && (
                  <div className="col-span-2">
                    <label className="text-[10px] text-gray-500 block mb-0.5 uppercase">Fielder</label>
                    <select
                      value={ballData.fielder_id || ''}
                      onChange={e => setBallData(prev => ({ ...prev, fielder_id: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                      data-testid="fielder-select"
                    >
                      <option value="">Select fielder...</option>
                      {bowlingRoster.map(p => <option key={p.id} value={p.id}>{p.player_name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}

          {/* Commentary — collapsed by default, toggled inline to keep viewport tight */}
          <details className="group">
            <summary className="text-[11px] text-gray-500 cursor-pointer list-none flex items-center gap-1.5 select-none">
              <i className="ph-bold ph-caret-right group-open:rotate-90 transition-transform text-xs"></i>
              <span className="uppercase tracking-wide">Commentary (optional)</span>
            </summary>
            <input
              type="text"
              value={ballData.commentary}
              onChange={e => setBallData(prev => ({ ...prev, commentary: e.target.value }))}
              placeholder="Add custom commentary..."
              className="mt-1.5 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </details>

          {/* End Innings — tucked in as a low-emphasis chip */}
          <div className="pt-2 border-t border-gray-100 flex items-center justify-end">
            <button
              onClick={() => setShowEndInningsModal(true)}
              disabled={loading}
              className="text-xs text-gray-500 hover:text-red-600 font-medium flex items-center gap-1"
              data-testid="end-innings-btn"
            ><i className="ph-bold ph-flag-checkered"></i>End Innings</button>
          </div>
        </div>
      )}

      {/* Sticky bottom action bar — Undo + Record Ball always reachable without scroll.
          When any blocking modal is open we dim it and disable pointer events so a
          stray tap on Record Ball can't sneak past the modal backdrop. */}
      {canEnterBall && (() => {
        const anyModalOpen = !!(editingBall || editBatsmanState || showChangeBowler || showEndInningsModal || showSquadSwap);
        return (
        <div
          className={`fixed left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 px-3 pt-2 flex gap-2 transition-opacity duration-150 ${anyModalOpen ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}
          style={{
            bottom: 'var(--bottom-nav-h)',
            zIndex: 1001,
            paddingBottom: '0.5rem',
          }}
          aria-hidden={anyModalOpen}
          data-testid="scorer-action-bar"
        >
          <button
            onClick={handleUndoBall}
            disabled={loading || anyModalOpen}
            className="px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
            data-testid="undo-btn"
          ><i className="ph-bold ph-arrow-counter-clockwise"></i>Undo</button>
          <button
            onClick={handleRecordBall}
            disabled={loading || anyModalOpen}
            className="flex-1 py-2.5 bg-[#3AAFA9] text-white font-bold rounded-lg hover:bg-[#2D8A85] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="record-ball-btn"
          >
            {loading
              ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
              : <><i className="ph-bold ph-check"></i>Record Ball</>}
          </button>
        </div>
        );
      })()}

      {/* Ball Edit Modal */}
      {editingBall && (
        <BallEditModal
          ball={editingBall}
          onSubmit={(overrides) => handleEditBall(editingBall, overrides)}
          onDelete={() => handleDeleteBall(editingBall)}
          onCancel={() => setEditingBall(null)}
          loading={loading}
        />
      )}

      {/* Mid-match Squad Swap Modal */}
      {showSquadSwap && (
        <SquadSwapModal
          eventId={eventId}
          fixtureId={fixtureId}
          onClose={() => setShowSquadSwap(false)}
          onUpdated={fetchData}
        />
      )}

      {/* Edit / Replace Batsman Modal */}
      {editBatsmanState && currentInnings && (() => {
        const wrongFacedBalls = allInningsBalls.some(
          b => b.batsman_id === editBatsmanState.wrong_id || b.non_striker_id === editBatsmanState.wrong_id
        );
        // Eligible replacement = batting roster minus current striker, non-striker, and anyone who already participated
        const participatedIds = new Set();
        allInningsBalls.forEach(b => {
          if (b.batsman_id) participatedIds.add(b.batsman_id);
          if (b.non_striker_id) participatedIds.add(b.non_striker_id);
          if (b.dismissed_player_id) participatedIds.add(b.dismissed_player_id);
        });
        const eligibleReplacements = battingRoster.filter(p =>
          p.id !== currentInnings.striker_id &&
          p.id !== currentInnings.non_striker_id &&
          !participatedIds.has(p.id)
        );
        return (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]"
            onClick={() => setEditBatsmanState(null)}
            data-testid="edit-batsman-modal"
          >
            <div
              className="bg-white rounded-2xl p-5 w-full max-w-sm mx-4 shadow-xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-1">
                <h3 className="font-bold text-lg text-[#2B3A67]">Edit Batsman</h3>
                <button
                  onClick={() => setEditBatsmanState(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1"
                  aria-label="Close"
                  data-testid="edit-batsman-close"
                >×</button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Currently at the crease: <span className="font-semibold text-gray-700">{editBatsmanState.wrong_name}</span>
              </p>

              {!wrongFacedBalls && (
                <button
                  onClick={() => handleUnsetBatsman(editBatsmanState.position)}
                  disabled={loading}
                  className="w-full mb-3 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium hover:bg-amber-100 disabled:opacity-50"
                  data-testid="unset-batsman-btn"
                >
                  <i className="ph-bold ph-x-circle mr-1.5"></i>Remove from crease (no balls faced)
                </button>
              )}

              <div className="border-t border-gray-100 pt-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">
                  {wrongFacedBalls
                    ? 'This batsman has already faced balls. Pick the player who should have been here — every ball will be retroactively credited to them.'
                    : 'Or replace with another player'}
                </p>
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {eligibleReplacements.length === 0 ? (
                    <div className="text-center text-sm text-gray-400 py-3">No eligible replacements available.</div>
                  ) : eligibleReplacements.map(p => (
                    <button
                      key={p.id}
                      onClick={() => handleReplaceBatsman(editBatsmanState.wrong_id, p.id)}
                      disabled={loading}
                      className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg text-sm hover:border-[#3AAFA9] hover:bg-[#3AAFA9]/5 disabled:opacity-50"
                      data-testid={`replace-batsman-${p.id}`}
                    >
                      <div className="font-medium text-gray-800 truncate">{p.player_name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setEditBatsmanState(null)}
                className="w-full mt-3 py-2 text-gray-500 text-sm font-medium hover:text-gray-700"
                data-testid="edit-batsman-cancel"
              >Cancel</button>
            </div>
          </div>
        );
      })()}

      {/* Mid-over Change Bowler Modal */}
      {showChangeBowler && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]"
          onClick={() => setShowChangeBowler(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="change-bowler-modal"
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-bold text-lg text-[#2B3A67]">Change Bowler (Mid-Over)</h3>
              <button
                onClick={() => setShowChangeBowler(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1"
                aria-label="Close"
                data-testid="change-bowler-close"
              >×</button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Current bowler will be replaced for the remaining balls of this over. Previous-over bowler remains excluded.</p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {eligibleBowlers.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-4">No eligible bowlers available.</div>
              ) : eligibleBowlers.map(b => (
                <button
                  key={b.player_pool_id}
                  onClick={() => handleChangeBowlerMidOver(b.player_pool_id)}
                  disabled={loading}
                  className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg text-sm hover:border-amber-400 hover:bg-amber-50 disabled:opacity-50"
                  data-testid={`change-bowler-option-${b.player_pool_id}`}
                >
                  <div className="font-medium text-gray-800">{b.player_name}</div>
                  <div className="text-[11px] text-gray-500">Overs: {b.overs_bowled} / {b.max_overs}</div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowChangeBowler(false)}
              className="w-full mt-3 py-2 text-gray-500 text-sm font-medium hover:text-gray-700"
            >Cancel</button>
          </div>
        </div>
      )}

      {/* End Innings Modal */}
      {showEndInningsModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]"
          onClick={() => setShowEndInningsModal(false)}
          data-testid="end-innings-modal"
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-bold text-lg text-[#2B3A67]">End Innings</h3>
              <button
                onClick={() => setShowEndInningsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1"
                aria-label="Close"
                data-testid="end-innings-close"
              >×</button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Select the reason for ending this innings:</p>
            <div className="space-y-2">
              {[
                { value: 'all_out', label: 'All Out', icon: 'ph-x-circle', color: 'bg-red-50 text-red-700 hover:bg-red-100' },
                { value: 'overs_complete', label: 'Overs Complete', icon: 'ph-check-circle', color: 'bg-green-50 text-green-700 hover:bg-green-100' },
                { value: 'declared', label: 'Declared', icon: 'ph-megaphone', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
                { value: 'rain', label: 'Rain', icon: 'ph-cloud-rain', color: 'bg-sky-50 text-sky-700 hover:bg-sky-100' },
                { value: 'bad_light', label: 'Bad Light', icon: 'ph-lightbulb', color: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
                { value: 'abandoned', label: 'Abandoned', icon: 'ph-prohibit', color: 'bg-gray-50 text-gray-700 hover:bg-gray-200' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleEndInnings(opt.value)}
                  disabled={loading}
                  className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm flex items-center gap-3 transition-colors ${opt.color}`}
                  data-testid={`end-reason-${opt.value}`}
                >
                  <i className={`ph-bold ${opt.icon} text-lg`}></i>{opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowEndInningsModal(false)}
              className="w-full mt-4 py-2 text-gray-500 text-sm font-medium hover:text-gray-700"
              data-testid="end-innings-cancel"
            >Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tie Pending Panel (shown when regulation or super over ended tied) ───
function TiePendingPanel({ eventId, fixtureId, loading, onAccept, onSuperOver }) {
  return (
    <div
      className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-5 space-y-4"
      data-testid="tie-pending-panel"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white">
          <i className="ph-bold ph-scales text-xl"></i>
        </div>
        <div>
          <h3 className="font-bold text-amber-900">Match Tied</h3>
          <p className="text-xs text-amber-700 mt-0.5">
            Both teams finished level. You can trigger a Super Over until there's a result, or accept the tie now.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={onSuperOver}
          disabled={loading}
          className="py-3 px-4 bg-[#3AAFA9] text-white font-bold rounded-lg hover:bg-[#2D8A85] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="trigger-super-over-btn"
        >
          <i className="ph-bold ph-lightning"></i> Trigger Super Over
        </button>
        <button
          onClick={onAccept}
          disabled={loading}
          className="py-3 px-4 bg-white border-2 border-amber-400 text-amber-700 font-bold rounded-lg hover:bg-amber-100 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="accept-tie-btn"
        >
          <i className="ph-bold ph-handshake"></i> Accept Tie (End Match)
        </button>
      </div>
      <p className="text-[11px] text-amber-600 text-center">
        <i className="ph-bold ph-info mr-1"></i>
        In a Super Over, the team that bowled last bats first. 1 over, max 2 wickets per side.
      </p>
    </div>
  );
}

// ── Reopened Match Panel ────────────────────────────────────────────────
// Shown when a non-tied match has been reopened for corrections.
// (Tied matches still render TiePendingPanel above.)
function ReopenedMatchPanel({
  fixture, teams = [], innings, teamARuns, teamBRuns, loading, onFinalise,
  eventId, fixtureId, onBallClick,
}) {
  const teamAName = teams.find((t) => t.id === fixture?.team_a_id)?.name || fixture?.team_a_name || 'Team A';
  const teamBName = teams.find((t) => t.id === fixture?.team_b_id)?.name || fixture?.team_b_name || 'Team B';
  const aWickets = innings
    .filter((i) => i.batting_team_id === fixture?.team_a_id)
    .reduce((s, i) => s + (i.total_wickets || 0), 0);
  const bWickets = innings
    .filter((i) => i.batting_team_id === fixture?.team_b_id)
    .reduce((s, i) => s + (i.total_wickets || 0), 0);

  // Per-innings ball cache + which innings card is expanded
  const [ballsByInnings, setBallsByInnings] = useState({});
  const [openInningsId, setOpenInningsId] = useState(null);
  const [fetchingId, setFetchingId] = useState(null);

  const toggleInnings = async (inn) => {
    if (openInningsId === inn.id) { setOpenInningsId(null); return; }
    setOpenInningsId(inn.id);
    if (ballsByInnings[inn.id]) return;
    setFetchingId(inn.id);
    try {
      const res = await axiosInstance.get(
        `/v1/events/${eventId}/live/${fixtureId}/innings/${inn.id}/balls`,
      );
      setBallsByInnings((prev) => ({ ...prev, [inn.id]: res.data || [] }));
    } catch {
      toast.error('Failed to load balls for this innings');
      setBallsByInnings((prev) => ({ ...prev, [inn.id]: [] }));
    } finally {
      setFetchingId(null);
    }
  };

  const sortedInnings = [...innings].sort((a, b) => (a.innings_number || 0) - (b.innings_number || 0));

  return (
    <div
      className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-5 space-y-4"
      data-testid="reopened-match-panel"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
          <i className="ph-bold ph-arrow-clockwise text-xl"></i>
        </div>
        <div>
          <h3 className="font-bold text-blue-900">Match Reopened</h3>
          <p className="text-xs text-blue-700 mt-0.5">
            Tap any ball below to edit or delete it. When done, hit
            <b> Finalise </b>to recompute the result.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg p-3 border border-blue-200">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{teamAName}</p>
            <p className="font-extrabold text-blue-900" data-testid="reopened-team-a-score">
              {teamARuns}/{aWickets}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{teamBName}</p>
            <p className="font-extrabold text-blue-900" data-testid="reopened-team-b-score">
              {teamBRuns}/{bWickets}
            </p>
          </div>
        </div>
      </div>

      {/* Per-innings ball editor */}
      <div className="space-y-2" data-testid="reopened-innings-list">
        {sortedInnings.map((inn) => {
          const battingTeamName = teams.find((t) => t.id === inn.batting_team_id)?.name || `Innings ${inn.innings_number}`;
          const isOpen = openInningsId === inn.id;
          const balls = ballsByInnings[inn.id] || [];
          const overs = {};
          balls.forEach((b) => {
            if (!overs[b.over_number]) overs[b.over_number] = [];
            overs[b.over_number].push(b);
          });
          const overNums = Object.keys(overs).map(Number).sort((a, b) => a - b);

          return (
            <div key={inn.id} className="bg-white rounded-xl border border-blue-200">
              <button
                onClick={() => toggleInnings(inn)}
                className="w-full flex items-center justify-between p-3 text-left"
                data-testid={`reopened-innings-toggle-${inn.id}`}
              >
                <span className="text-sm font-bold text-blue-900">
                  Innings {inn.innings_number} · {battingTeamName}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {inn.total_runs || 0}/{inn.total_wickets || 0}
                  </span>
                  <i className={`ph-bold ph-caret-${isOpen ? 'up' : 'down'} text-blue-700`}></i>
                </div>
              </button>

              {isOpen && (
                <div className="px-3 pb-3 space-y-2 border-t border-blue-100">
                  {fetchingId === inn.id ? (
                    <p className="text-xs text-gray-500 py-3 text-center">Loading balls...</p>
                  ) : balls.length === 0 ? (
                    <p className="text-xs text-gray-500 py-3 text-center">No balls recorded.</p>
                  ) : (
                    overNums.map((overNum) => (
                      <div
                        key={overNum}
                        className="flex items-center gap-2"
                        data-testid={`reopened-over-${inn.id}-${overNum}`}
                      >
                        <span className="text-[10px] font-bold text-blue-600 w-12 shrink-0 uppercase tracking-wider">
                          Ov {overNum + 1}
                        </span>
                        <OverVisualization balls={overs[overNum]} onBallClick={onBallClick} />
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={onFinalise}
        disabled={loading}
        className="w-full py-3 px-4 bg-[#3AAFA9] text-white font-bold rounded-lg hover:bg-[#2D8A85] disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="finalise-match-btn"
      >
        <i className="ph-bold ph-check-circle"></i>
        {loading ? 'Finalising...' : 'Finalise Match'}
      </button>

      <p className="text-[11px] text-blue-600 text-center">
        <i className="ph-bold ph-info mr-1"></i>
        Finalising recomputes the winner, standings, awards and stats from the
        current ball-by-ball data.
      </p>
    </div>
  );
}
